import {
  TProfileDetails,
  TProfileDetailsUI,
  TProfilePictureUpdateUI,
  TNameUpdateUI,
  TEmailUpdateUI,
  TPhoneUpdateUI,
  TApplicationProfile,
  TApplicationProfileUI,
  TMetadataUpdateUI,
  TSaMetadataUpdateUI,
  TTenantPropertyDB,
  TTenantProperty,
  TTenantCreateUI,
  TTenantDataUI,
  TTenantDataFromDb,
  TCheckTenantDuplicateUI,
  TDuplicateTenantDB,
  TTenantHpDocDataUI,
  TTenantHpDocDataDb,
} from 'data/types/tenant.types';
import { ETenantUrls } from 'data/urls/tenant.url';
import { EApplicationUrls } from 'data/urls/application.url';
import {
  TAnimalFieldVisibilityDB,
  TAnimalFieldVisibilityUI,
} from 'data/types/animalFieldVisibility.types';
import { animalFieldVisibilityConverter } from 'data/convertors/animalFieldVisibility.convertors';
import {
  createTenantProfileConvertor,
  getTenantConvertor,
  getTenantProfileConvertor,
  tenantProfilePictureUpdateConverter,
  tenantNameUpdateConverter,
  tenantEmailUpdateConvertor,
  tenantPhoneUpdateConvertor,
  applicationProfileConvertor,
  metadataUpdateConvertor,
  saMetadataUpdateConvertor,
  onboardTenantConvertor,
  getTenantPropertiesConvertor,
  propertyInfoConverter,
  duplicateTenantConverter,
  hpDocsTenantConverter,
} from 'data/convertors/tenant.convertors';
import {
  TRegisterAnimalUI,
  TUploadableVetRecordTypeDB,
} from 'data/types/animal.types';
import { authSplitApi } from '../helpers/slice.helpers';

const baseUrl = `${ETenantUrls.TENANT_PROFILE}`;

export const tenantApi = authSplitApi('tenant', [
  'tenant',
  'details',
  'profilePicture',
  'name',
  'email',
  'phone',
  'applicationProfile',
  'tenant-hp-docs',
]).injectEndpoints({
  endpoints: build => ({
    getTenantProfile: build.query<TTenantDataUI, string | null>({
      query() {
        return {
          url: `${baseUrl}`,
          method: 'GET',
        };
      },
      providesTags: ['tenant'],
      transformResponse: (data: TTenantDataFromDb) => {
        if (!data) {
          return null;
        }
        return getTenantProfileConvertor.fromDb(data);
      },
    }),
    getProfileDetails: build.query<TProfileDetailsUI, null | void>({
      query() {
        return {
          url: `${baseUrl}/${ETenantUrls.DETAILS}`,
          method: 'GET',
        };
      },
      providesTags: ['details'],
      transformResponse: (data: TProfileDetails) => {
        return getTenantConvertor.fromDb(data);
      },
    }),
    getTenantProperties: build.query<TTenantProperty[], void>({
      query() {
        return {
          url: `${baseUrl}/${ETenantUrls.PROPERTIES}`,
          method: 'GET',
        };
      },
      providesTags: ['properties'],
      transformResponse: (data: TTenantPropertyDB[]) => {
        return getTenantPropertiesConvertor.fromDb(data);
      },
    }),
    getApplicationProfile: build.query<
      TApplicationProfileUI,
      { applicationId: string }
    >({
      query({ applicationId }) {
        return {
          url: `${EApplicationUrls.APPLICATION}/${EApplicationUrls.STEP}/${EApplicationUrls.TENANT}`,
          method: 'GET',
          params: { applicationId },
        };
      },
      transformResponse: (data: TApplicationProfile) =>
        applicationProfileConvertor.fromDb(data),
      providesTags: ['applicationProfile'],
    }),
    updateProfilePicture: build.mutation<void, TProfilePictureUpdateUI>({
      query(data) {
        return {
          url: `${baseUrl}/${ETenantUrls.PROFILE_PICTURE}`,
          method: 'PATCH',
          body: tenantProfilePictureUpdateConverter.toDb(data),
        };
      },
      invalidatesTags: ['tenant', 'details', 'applicationProfile'],
    }),
    updateName: build.mutation<void, TNameUpdateUI>({
      query(data) {
        return {
          url: `${baseUrl}/${ETenantUrls.NAME}`,
          method: 'PATCH',
          body: tenantNameUpdateConverter.toDb(data),
        };
      },
      invalidatesTags: ['details', 'applicationProfile'],
    }),
    updateEmail: build.mutation<void, TEmailUpdateUI>({
      query(data) {
        return {
          url: `${baseUrl}/${ETenantUrls.EMAIL}`,
          method: 'PATCH',
          body: tenantEmailUpdateConvertor.toDb(data),
        };
      },
      invalidatesTags: ['details', 'applicationProfile'],
    }),
    updatePhone: build.mutation<void, TPhoneUpdateUI>({
      query(data) {
        return {
          url: `${baseUrl}/${ETenantUrls.PHONE}`,
          method: 'PATCH',
          body: tenantPhoneUpdateConvertor.toDb(data),
        };
      },
      invalidatesTags: ['details', 'applicationProfile'],
    }),
    createTenantProfile: build.mutation<void, TTenantCreateUI>({
      query(body) {
        return {
          url: `${baseUrl}`,
          method: 'POST',
          body: createTenantProfileConvertor.toDb(body),
        };
      },
      invalidatesTags: ['tenant'],
    }),
    onboardTenant: build.mutation<void, TTenantCreateUI>({
      query(body) {
        return {
          url: `${baseUrl}/${ETenantUrls.ONBOARD}`,
          method: 'PATCH',
          body: onboardTenantConvertor.toDb(body),
        };
      },
      invalidatesTags: ['tenant'],
    }),
    updateMetadata: build.mutation<void, TMetadataUpdateUI>({
      query(body) {
        return {
          url: `${baseUrl}/${ETenantUrls.ANIMAL_METADATA}/has-letter`,
          method: 'PATCH',
          body: metadataUpdateConvertor.toDb(body),
        };
      },
      invalidatesTags: ['applicationProfile'],
    }),
    updateSaMetadata: build.mutation<void, TSaMetadataUpdateUI>({
      // Observabale/Non Observavbale API
      query(body) {
        return {
          url: `${baseUrl}/${ETenantUrls.ANIMAL_METADATA}/${ETenantUrls.SA}`,
          method: 'PATCH',
          body: saMetadataUpdateConvertor.toDb(body),
        };
      },
      invalidatesTags: ['applicationProfile'],
    }),
    registerAnimal: build.mutation<string, TRegisterAnimalUI>({
      query(body) {
        return {
          url: `${baseUrl}/${ETenantUrls.ANIMALS}/${ETenantUrls.REGISTER}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['animals'],
    }),
    checkTenantDuplicate: build.query<
      TDuplicateTenantDB,
      TCheckTenantDuplicateUI
    >({
      query(params) {
        return {
          url: `${baseUrl}/${ETenantUrls.DUPLICATIONS}`,
          method: 'GET',
          params: propertyInfoConverter.toDb(params),
        };
      },
      transformResponse: (data: TDuplicateTenantDB) => {
        return data && duplicateTenantConverter.fromDb(data);
      },
    }),
    tenantAttachTo: build.mutation<void, { destinationProfileId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${ETenantUrls.ATTACH_TO}`,
          method: 'PATCH',
          body,
        };
      },
    }),
    acknowledgeAgreement: build.mutation<void, { tenantResidencyId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${ETenantUrls.AGREEMENT}/${ETenantUrls.ACKNOWLEDGE}`,
          method: 'POST',
          body,
        };
      },
    }),
    getUploadableVetRecords: build.query<
      TUploadableVetRecordTypeDB[],
      { tenantResidencyId: string }
    >({
      query(params) {
        return {
          url: `${EApplicationUrls.APPLICATION}/${EApplicationUrls.STEP}/${EApplicationUrls.UPLOADABLE_VET_RECORDS}`,
          method: 'GET',
          params,
        };
      },
    }),
    getAnimalFieldVisibility: build.query<
      TAnimalFieldVisibilityUI,
      { tenantResidencyId: string }
    >({
      query(params) {
        return {
          url: `${EApplicationUrls.APPLICATION}/${EApplicationUrls.STEP}/${EApplicationUrls.ANIMAL_FIELD_VISIBILITY}`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (data: TAnimalFieldVisibilityDB) =>
        animalFieldVisibilityConverter.fromDb(data),
    }),
    getTenantHpDocs: build.query<TTenantHpDocDataUI, void>({
      query() {
        return {
          url: `${baseUrl}/hp-docs`,
          method: 'GET',
        };
      },
      providesTags: ['tenant-hp-docs'],
      transformResponse: (data: TTenantHpDocDataDb) => {
        if (!data) {
          return { hpDocuments: [], saDocumentPackets: [] };
        }
        return hpDocsTenantConverter.fromDb(data);
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTenantProfileQuery,
  useGetTenantPropertiesQuery,
  useCreateTenantProfileMutation,
  useOnboardTenantMutation,
  useGetProfileDetailsQuery,
  useGetApplicationProfileQuery,
  useUpdateProfilePictureMutation,
  useUpdateNameMutation,
  useUpdateEmailMutation,
  useUpdatePhoneMutation,
  useUpdateMetadataMutation,
  useUpdateSaMetadataMutation,
  useRegisterAnimalMutation,
  useLazyCheckTenantDuplicateQuery,
  useTenantAttachToMutation,
  useAcknowledgeAgreementMutation,
  useGetUploadableVetRecordsQuery,
  useGetAnimalFieldVisibilityQuery,
  useGetTenantHpDocsQuery,
} = tenantApi;
